import React, { useEffect, useRef, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { hasInvalidFields } from 'src/utils/aolFieldsHelper';
import { ElementWrapper, HighlightPanel } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { useRotationStore } from 'src/context/RotationContext';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationItemPlaceHolder } from 'src/types/ViewModel/Stowage/Rotation/RotationItemPlaceHolder';
import { isManualCall } from 'src/utils/stowage/stowageRotationUtils';

type AddPortVM = {
    portId: number;
    portName: string;
    portCallStatus: PortCallStatusEnum;
    portOrderNumber: number;
    voyageNumber: string;
    voyageStatus: string;
    notValidFieldsArray?: [];
    hasInvalidFields?: boolean;
};

type Props = {
    rotationItemPlaceHolder: RotationItemPlaceHolder;
    onClosePanel: () => void;
};

const PortCallRotationPanel: React.FC<Props> = ({ rotationItemPlaceHolder, onClosePanel }) => {
    const [portVM, setPortVM] = useState<AddPortVM>(null);
    const { activeStowagePlan } = useStowageStore();
    const { portNameList } = useRotationStore();
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const { saveStowageData } = usePostStowageApi();
    const { stowageRotationTree } = useRotationStore();
    const changedData = useRef<any>({});

    const isAddPort = rotationItemPlaceHolder?.type === 'add';
    const portStatusOptionsList = [
        { value: PortCallStatusEnum.Departed, label: 'Departed' },
        { value: PortCallStatusEnum.Planned, label: 'Planned' },
    ];

    useEffect(() => {
        if (!activeStowagePlan || !rotationItemPlaceHolder) return;

        setPortVM({
            portId: isAddPort ? 0 : rotationItemPlaceHolder.selectedPortCall.portId,
            portName: isAddPort ? '' : rotationItemPlaceHolder.selectedPortCall.portDisplayName,
            portCallStatus: getPortCallStatus(rotationItemPlaceHolder.selectedPortCall),
            portOrderNumber: rotationItemPlaceHolder.newPortCallOrderNumber,
            voyageNumber: rotationItemPlaceHolder.selectedPortCall.turnaroundVoyageNumber
                ? rotationItemPlaceHolder.selectedPortCall.turnaroundVoyageNumber
                : rotationItemPlaceHolder.selectedPortCall.voyageNumber,
            voyageStatus: 'Closed',
        });
    }, [activeStowagePlan, rotationItemPlaceHolder]);

    const getPortCallStatus = (portCall: RotationPortCall) => {
        if (!isAddPort) return portCall.portCallStatus;

        const { previousPortCall: prev, nextPortCall: next } = rotationItemPlaceHolder;
        const bothDeparted = prev?.portCallStatus === PortCallStatusEnum.Departed && next?.portCallStatus === PortCallStatusEnum.Departed;
        const bothPlanned = prev?.portCallStatus === PortCallStatusEnum.Planned && next?.portCallStatus === PortCallStatusEnum.Planned;

        if (bothDeparted || bothPlanned) {
            return prev?.portCallStatus;
        } else {
            return PortCallStatusEnum.Planned;
        }
    };

    const isPortStatusReadOnly = () => {
        const { previousPortCall: prev, nextPortCall: next } = rotationItemPlaceHolder;
        const bothDeparted = prev?.portCallStatus === PortCallStatusEnum.Departed && next?.portCallStatus === PortCallStatusEnum.Departed;
        const bothPlanned = prev?.portCallStatus === PortCallStatusEnum.Planned && next?.portCallStatus === PortCallStatusEnum.Planned;

        if (isAddPort && (bothDeparted || bothPlanned)) {
            return true;
        }
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const {
            target: { name, value, type },
        } = changeObject.event;
        const normalizedValue = type === 'select-one' ? (value === 'Select port' ? 0 : +value || '') : value ? +value : 0;

        changedData.current[name] = Number(normalizedValue);
        portVM[name] = Number(normalizedValue);

        hasInvalidFields(portVM, name, changeObject.isValid);
        setPortVM({ ...portVM });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const createAddPortCallChangeData = () => {
        return {
            portId: +portVM.portId,
            portCallStatus: portVM.portCallStatus,
            portOrderNumber: portVM.portOrderNumber,
            voyageNumber: portVM.voyageNumber,
            voyageStatus: portVM.voyageStatus,
            dataSource: 'OrcaWeb',
        };
    };

    const updatePortCallStatuses = (portCallIndex: number, newStatus: PortCallStatusEnum): Array<any> => {
        const changes = [];

        if (newStatus === PortCallStatusEnum.Planned) {
            // Update all subsequent departed ports to planned
            stowageRotationTree
                .slice(portCallIndex + 1)
                .filter((portCall) => portCall.portCallStatus === PortCallStatusEnum.Departed)
                .forEach((portCall) => {
                    changes.push(
                        createStowagePlanChange(activeStowagePlan.stowagePlanGuid, portCall.portCallGuid, StowageChangeDataType.RotationPortCall, {
                            portCallStatus: PortCallStatusEnum.Planned,
                        })
                    );
                });
        } else if (newStatus === PortCallStatusEnum.Departed) {
            // Update all previous planned ports to departed
            stowageRotationTree
                .slice(0, portCallIndex)
                .filter((portCall) => portCall.portCallStatus === PortCallStatusEnum.Planned)
                .forEach((portCall) => {
                    changes.push(
                        createStowagePlanChange(activeStowagePlan.stowagePlanGuid, portCall.portCallGuid, StowageChangeDataType.RotationPortCall, {
                            portCallStatus: PortCallStatusEnum.Departed,
                        })
                    );
                });
        }

        return changes;
    };

    const onSave = async () => {
        if (!canSave) {
            console.warn('Cannot save: invalid or incomplete data');
            return;
        }

        const portCallGuid = isAddPort ? crypto.randomUUID() : rotationItemPlaceHolder.selectedPortCall.portCallGuid;
        const changeData = isAddPort ? createAddPortCallChangeData() : changedData.current;

        // Collect all changes
        const stowagePlanChanges = [
            // Add the main port call change
            createStowagePlanChange(activeStowagePlan.stowagePlanGuid, portCallGuid, StowageChangeDataType.RotationPortCall, changeData),
            // Add status updates for other port calls
            ...updatePortCallStatuses(rotationItemPlaceHolder.placeHolderPortCallIndex, portVM.portCallStatus),
        ];

        await saveStowageData(activeStowagePlan, stowagePlanChanges);
        changedData.current = {};
        onClosePanel();
    };

    const getPortOptionsList = () => {
        if (portNameList && portNameList.length > 0) {
            return portNameList
                .filter((p) => !p.isDeleted)
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                .map((portItem) => {
                    return { value: portItem.portId, label: portItem.name };
                });
        }
    };

    const getPortName = (portId: number) => portNameList?.find((portItem) => portItem.portId === portId)?.name;
    const getPortStatus = (portStatus: PortCallStatusEnum) => (portStatus === PortCallStatusEnum.Departed ? 'Departed' : 'Planned');

    const canEdit = () => {
        if (isAddPort) return false;
        return !(isManualCall(rotationItemPlaceHolder.selectedPortCall) && !rotationItemPlaceHolder.selectedPortCall.berthCalls?.length);
    };
    const canSave = portVM?.portId > 0 && !portVM.hasInvalidFields;

    return (
        rotationItemPlaceHolder &&
        portVM && (
            <OdfjellPanel
                headerTitle={isAddPort ? 'Add port call' : 'Edit port call'}
                panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
                defaultPosition="center"
                onCancel={onClosePanel}
                onAction={onSave}
                showCancelButton={true}
                isActionDisabled={!canSave}
            >
                <HighlightPanel>
                    <div className="left-wrapper">
                        <ElementWrapper>
                            <OdfjellInputComponent
                                isReadOnly={canEdit()}
                                dataObject={portVM}
                                extraArgs={0}
                                description={'Port'}
                                type={'select'}
                                options={getPortOptionsList()}
                                value={canEdit() ? getPortName(portVM.portId) : portVM.portId}
                                name={'portId'}
                                onChange={onInputChange}
                                disableUnit={true}
                                selectDefaultOption={'Select port'}
                            />
                        </ElementWrapper>
                    </div>
                    <div className="right-wrapper">
                        <ElementWrapper>
                            <OdfjellInputComponent
                                isReadOnly={true}
                                description={'Voyage'}
                                value={portVM.voyageNumber}
                                name={'voyageNumber'}
                                type={'text'}
                                tabIndex={1}
                                disableUnit={true}
                                key={'input_voyage_number'}
                            />
                        </ElementWrapper>
                    </div>
                    <div className="left-wrapper">
                        <ElementWrapper>
                            <OdfjellInputComponent
                                isReadOnly={isPortStatusReadOnly()}
                                type={'select'}
                                description={'Status'}
                                value={isPortStatusReadOnly() ? getPortStatus(portVM.portCallStatus) : portVM.portCallStatus}
                                name={'portCallStatus'}
                                dataObject={portVM}
                                leaveBlank={true}
                                options={portStatusOptionsList}
                                onChange={onInputChange}
                                tabIndex={1}
                                disableUnit={true}
                                key={'input_port_call_status'}
                            />
                        </ElementWrapper>
                    </div>
                </HighlightPanel>
                {rotationItemPlaceHolder.type === 'add' && (
                    <div>
                        Adding port call after <b>{rotationItemPlaceHolder.selectedPortCall.portDisplayName}</b>
                    </div>
                )}
            </OdfjellPanel>
        )
    );
};

export default PortCallRotationPanel;
